import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import ExportIcon from "src/core/assets/images/icons/export.svg?react";
import { FormUserSearch } from "src/core/components/custom-ui/form-user-search";
import { Button } from "src/core/components/ui/button";
import { Checkbox } from "src/core/components/ui/checkbox";
import { Form } from "src/core/components/ui/form";
import { Status } from "src/core/constants/folders";
import { Roles } from "src/core/constants/roles";
import { useDownloadPdf } from "src/core/hooks/folders-list/useDownloadPdf";
import { toast } from "src/core/hooks/use-toast";
import { useFolderListFilter } from "src/core/hooks/useFolderListFilter";
import { cn } from "src/core/lib/utils";

type FoldersListFiltersProps = {
  status?: Status;
  disabled?: boolean;
};

const COLLABORATOR_GROUPS = [Roles.COLLABORATEUR];

export const FoldersListFilters = ({ status, disabled = false }: FoldersListFiltersProps) => {
  const { t } = useTranslation();
  const { form, labels, rememberLabel, isMyFoldersDisabled, areSelectsDisabled } =
    useFolderListFilter();

  const { downloadValidatedFoldersPdf, isLoading } = useDownloadPdf();

  const isCheckboxDisabled = disabled || isMyFoldersDisabled;
  const areFiltersSelectsDisabled = disabled || areSelectsDisabled;
  const isDownloadDisabled = disabled || isLoading;
  const expertId = form.watch("expertComptable") || "";
  const collaboratorId = form.watch("collaborator") || "";
  const pinnedExpert = useMemo(
    () =>
      expertId && expertId !== "none" && labels.expertComptable
        ? [{ value: expertId, label: labels.expertComptable }]
        : [],
    [expertId, labels.expertComptable]
  );
  const pinnedCollaborator = useMemo(
    () =>
      collaboratorId && collaboratorId !== "none" && labels.collaborator
        ? [{ value: collaboratorId, label: labels.collaborator }]
        : [],
    [collaboratorId, labels.collaborator]
  );

  const handleDownload = async () => {
    try {
      await downloadValidatedFoldersPdf();
    } catch (error: unknown) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : t("home.foldersListFilters.downloadError", {
              defaultValue: "Erreur lors du téléchargement du PDF",
            });

      toast({
        description: message,
        variant: "destructive",
      });
    }
  };

  return (
    <Form {...form}>
      <div
        className={cn(
          "flex flex-row gap-x-30 items-center ml-15 mb-20 w-full",
          disabled && "opacity-60"
        )}
        aria-busy={disabled}
        data-testid="folders-list-filters"
      >
        <div className="flex flex-row items-center w-full">
          <div className="flex flex-row items-center gap-x-5 min-w-[110px]">
            <Checkbox
              id="myFolders"
              className={cn(
                "text-blue-primary bg-white",
                (isMyFoldersDisabled || disabled) && "border-2 border-muted-foreground bg-disabled"
              )}
              checked={form.watch("myFolders") || false}
              onCheckedChange={(checked) => {
                if (isCheckboxDisabled) return;
                form.setValue("myFolders", checked === true);
              }}
              disabled={isCheckboxDisabled}
            />
            <label
              htmlFor="myFolders"
              className={cn("font-light", isCheckboxDisabled && "opacity-50")}
            >
              {t("home.foldersListFilters.myFolders")}
            </label>
          </div>

          <FormUserSearch
            name="expertComptable"
            control={form.control}
            label={t("home.foldersListFilters.expertComptableLabel")}
            pinnedOptions={pinnedExpert}
            placeholder={t("global.form.select.placeholder")}
            className="w-[200px]"
            disabled={areFiltersSelectsDisabled}
            clearable
            onSelected={(option) => rememberLabel("expertComptable", option?.label ?? "")}
            wrapperClassName="flex flex-row items-center gap-x-10 ml-[100px] mr-40"
          />

          <FormUserSearch
            name="collaborator"
            control={form.control}
            allowedGroups={COLLABORATOR_GROUPS}
            label={t("home.foldersListFilters.collaboratorLabel")}
            pinnedOptions={pinnedCollaborator}
            placeholder={t("global.form.select.placeholder")}
            className="w-[200px]"
            disabled={areFiltersSelectsDisabled}
            clearable
            onSelected={(option) => rememberLabel("collaborator", option?.label ?? "")}
            wrapperClassName="flex flex-row items-center gap-x-10"
          />
        </div>

        {status === Status.VALIDATED && (
          <Button onClick={handleDownload} loading={isLoading} disabled={isDownloadDisabled}>
            <ExportIcon />
            {t("home.foldersListFilters.download")}
          </Button>
        )}
      </div>
    </Form>
  );
};
